<template>
  <CCard border-color="light" class="page-promotion-sort">
    <CCardBody>
      <!-- ═══ HEADER ═══ -->
      <div class="row">
        <div class="col-12 d-flex justify-content-between">
          <CButton color="light" @click="$router.push('/promotion')" class="align-items-center pr-4 pl-4">
            <span><i class="fi fi-rr-arrow-left mr-2" style="position: relative; top: 2px;"></i></span>{{ $t('back') }}
          </CButton>
          <div class="d-flex align-items-center justify-content-between promo-auto-box">
            <div class="mr-2">
              <div class="font-weight-bold">
                {{ $t('promotionAutoApply') }}
              </div>
            </div>
            <CSwitch
              color="warning"
              size="lg"
              :checked.sync="isApplyPromotionAuto"
              @update:checked="updateAutoApplyPromotion"
            />
          </div>
        </div>
        <div class="col-12 text-center mt-4">
          <h3>{{ $t('promotionSortTitle') }}</h3>
          <span>{{ $t('promotionSortDescription') }}</span>
        </div>
      </div>

      <!-- ═══ INFO BOX ═══ -->
      <div class="mb-2 mt-2">
        <CAlert color="info" class="mb-3 promo-info-box">
          <div class="d-flex">
            <div class="mr-3">
              <i class="fi fi-rr-info" style="font-size:20px;"></i>
            </div>
            <div>
              <div class="promo-info-title">{{ $t('promotionInfo.title') }}</div>
              <div class="promo-info-desc">{{ $t('promotionInfo.description') }}</div>
              <div class="promo-steps mt-2">
                <div class="promo-step" v-for="n in 4" :key="n">
                  <span class="mr-2">{{ n }}.</span>
                  {{ $t(`promotionInfo.step${n}`) }}
                  <span class="step-desc">{{ $t(`promotionInfo.step${n}Desc`) }}</span>
                </div>
              </div>
              <small class="text-info">* {{ $t('promotionInfo.note1') }}</small>
              <small class="text-info d-block">** {{ $t('promotionInfo.note2') }}</small>
            </div>
          </div>
        </CAlert>
      </div>

      <!-- ═══ TABS (v-for — ลดซ้ำจาก 4 ที่เหลือ 1) ═══ -->
      <CTabs justified fill>
        <CTab v-for="tab in tabConfigs" :key="tab.key">
          <template #title>
            <div class="d-flex align-items-center promo-tab-title">
              <i :class="tab.icon" class="mr-2 mt-1"></i>
              {{ $t(tab.label) }}
              <CBadge :color="tab.badgeColor" shape="pill" class="ml-2">
                {{ filteredTab(tab.key).length || 0 }}
              </CBadge>
            </div>
          </template>

          <div class="mt-3">
            <!-- Draggable List -->
            <VueDraggable
              v-if="filteredTab(tab.key).length"
              v-model="promotionTabs[tab.key]"
              item-key="objectId"
              handle=".drag-handle"
              ghost-class="drag-ghost"
              class="promotion-list"
              @end="onDragEnd(tab.group)"
            >
              <div
                v-for="(item, index) in filteredTab(tab.key)"
                :key="item.objectId || index"
              >
                <div
                  class="promo-card d-flex align-items-center"
                  :class="{ 'promo-card--expired': isExpired(item.raw) }"
                >
                  <!-- Drag Handle -->
                  <div class="drag-handle drag-handle-icon mr-3">☰</div>

                  <!-- Image -->
                  <img
                    class="promo-image drag-handle"
                    :src="item.raw?.details?.remoteImagePath || '/img/default-promo.png'"
                  />

                  <!-- Content -->
                  <div class="promo-content flex-grow-1 ml-3 drag-handle">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="promo-title">{{ index + 1 }}. {{ item.title }}</div>
                        <div class="promo-type">{{ $t(item.type) }}</div>
                        <div class="promo-date">
                          <i class="fi fi-rr-calendar-clock mr-1"></i>
                          {{ formatDate(item.raw.startDate) }} - {{ formatDate(item.raw.endDate) }}
                        </div>
                      </div>

                      <!-- Status Badge -->
                      <div class="ml-2">
                        <CBadge
                          v-if="isExpired(item.raw)"
                          color="danger" shape="pill" class="sort-badge"
                        >
                          <i class="fi fi-rr-calendar-xmark mr-1"></i>{{ $t('promotionExpired') }}
                        </CBadge>
                        <CBadge
                          v-else-if="item.raw?.isActive"
                          color="success" shape="pill" class="sort-badge"
                        >
                          <i class="fi fi-rr-check-circle mr-1"></i>{{ $t('promotionActive') }}
                        </CBadge>
                        <CBadge
                          v-else
                          color="secondary" shape="pill" class="sort-badge"
                        >
                          {{ $t('promotionNotActive') }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </VueDraggable>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <i class="cil-tags empty-icon"></i>

              <div class="empty-title">
                {{ $t('emptyPromotion.title') }}
              </div>

              <div class="empty-desc">
                {{ $t('emptyPromotion.description') }}
              </div>

              <CButton
                color="primary"
                size="sm"
                class="mt-3"
                @click="$router.push('/promotion')"
              >
                + {{ $t('createPromotion') }}
              </CButton>
            </div>
          </div>
        </CTab>
      </CTabs>

    </CCardBody>
  </CCard>
</template>

<script>
import pos from '@/services/local'
import util from '@/util/util'
import '@/assets/styles/promotion.css'
import { VueDraggable } from 'vue-draggable-plus'

export default {
  name: 'PromotionSort',
  components: { VueDraggable },
  data() {
    const PROMOTION_GROUP = {
      ITEM: [
        'ITEM_FIXED_PRICE', 'ITEM_PERCENTAGE_DISCOUNT', 'ITEM_DOLLAR_DISCOUNT',
        'FREE_ITEM', 'QUANTITY_ITEM_EXCEED', 'PRICE_ITEM_EXCEED'
      ],
      CATEGORY: [
        'CATEGORY_FIXED_PRICE', 'CATEGORY_PERCENTAGE_DISCOUNT', 'CATEGORY_DOLLAR_DISCOUNT',
        'QUANTITY_CATEGORY_EXCEED', 'PRICE_CATEGORY_EXCEED'
      ],
      RECEIPT: [
        'RECEIPT_PERCENTAGE_DISCOUNT', 'RECEIPT_DOLLAR_DISCOUNT',
        'QUANTITY_RECEIPT_EXCEED', 'PRICE_RECEIPT_EXCEED'
      ],
      NO_CONDITION: ['NO_CONDITION']
    }

    return {
      PROMOTION_GROUP,
      orderShopCollection: null,
      isApplyPromotionAuto: false,
      statusFilter: 'all',

      tabConfigs: [
        { key: 'item', group: 'ITEM', icon: 'fi fi-rr-box', label: 'promotionSort.item', badgeColor: 'info' },
        { key: 'category', group: 'CATEGORY', icon: 'fi fi-rr-apps', label: 'promotionSort.category', badgeColor: 'primary' },
        { key: 'receipt', group: 'RECEIPT', icon: 'fi fi-rr-receipt', label: 'promotionSort.receipt', badgeColor: 'success' },
        { key: 'noCondition', group: 'NO_CONDITION', icon: 'fi fi-rr-minus-circle', label: 'promotionSort.none', badgeColor: 'secondary' },
      ],

      statusFilters: [
        { value: 'all', label: 'viewAllFood', fallback: 'ทั้งหมด', icon: 'fi fi-rr-layers', btnClass: '' },
        { value: 'active', label: 'promotionActive', fallback: 'ยังไม่หมดอายุ', icon: 'fi fi-rr-check-circle', btnClass: 'sort-filter-btn--green' },
        { value: 'expired', label: 'promotionExpired', fallback: 'หมดอายุแล้ว', icon: 'fi fi-rr-calendar-xmark', btnClass: 'sort-filter-btn--red' },
      ],

      promotionTabs: {
        item: [],
        category: [],
        receipt: [],
        noCondition: []
      },

      allPromotions: [] // เก็บ raw ทั้งหมดเพื่อนับ count
    }
  },

  computed: {
    shopObjectId() {
      return this.$store.getters.shopObjectId
    },
    uid() {
      return localStorage.getItem('uid')
    }
  },

  created() {
    this.fetchPromotions()
    this.loadOrderShopSetting()
  },

  methods: {
    // ═══ STATUS FILTER ═══
    changeStatusFilter(value) {
      this.statusFilter = value
      this.buildTabs()
    },

    getStatusCount(status) {
      const now = new Date()
      if (status === 'all') return this.allPromotions.length
      if (status === 'active') return this.allPromotions.filter(p => new Date(p.endDate) > now).length
      if (status === 'expired') return this.allPromotions.filter(p => new Date(p.endDate) <= now).length
      return 0
    },

    filteredTab(key) {
      return this.promotionTabs[key] || []
    },

    // ═══ FETCH & BUILD ═══
    fetchPromotions() {
      pos.get(`/api/v3.0/shop/promotions/${this.shopObjectId}`, {
        headers: {
          shopObjectId: util.sanitizeShopObjectId(this.shopObjectId),
          uid: this.uid,
        },
        params: {
          status: 'active',
        }
      }).then(res => {
        this.allPromotions = res.data?.data || []
        this.buildTabs()
      })
    },

    buildTabs() {
      this.promotionTabs = { item: [], category: [], receipt: [], noCondition: [] }

      this.allPromotions.forEach(promo => {
        const type = promo.type
        const baseData = {
          id: promo._id,
          objectId: promo.objectId,
          type: promo.type,
          title: promo.details?.title,
          itemSequence: promo.itemSequence || 9999,
          raw: promo
        }

        if (this.PROMOTION_GROUP.ITEM.includes(type)) {
          this.promotionTabs.item.push(baseData)
        } else if (this.PROMOTION_GROUP.CATEGORY.includes(type)) {
          this.promotionTabs.category.push(baseData)
        } else if (this.PROMOTION_GROUP.RECEIPT.includes(type)) {
          this.promotionTabs.receipt.push(baseData)
        } else if (this.PROMOTION_GROUP.NO_CONDITION.includes(type)) {
          this.promotionTabs.noCondition.push(baseData)
        }
      })

      // Sort by itemSequence
      Object.keys(this.promotionTabs).forEach(key => {
        this.promotionTabs[key].sort((a, b) => a.itemSequence - b.itemSequence)
      })
    },

    // ═══ HELPERS ═══
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString()
    },

    isExpired(promotion) {
      return new Date(promotion.endDate) < new Date()
    },

    // ═══ DRAG & DROP ═══
    onDragEnd(group) {
      const list = this.getGroupList(group)
      list.forEach((item, index) => {
        item.itemSequence = index + 1
      })
      this.saveOrder(group, list)
    },

    getGroupList(group) {
      const map = { ITEM: 'item', CATEGORY: 'category', RECEIPT: 'receipt', NO_CONDITION: 'noCondition' }
      return this.promotionTabs[map[group]] || []
    },

    async saveOrder(group, list) {
      try {
        const payload = {
          group,
          promotions: list.map(p => ({
            objectId: p.objectId,
            itemSequence: p.itemSequence
          }))
        }

        await pos.patch('/api/v3.0/shop/sequence/promotion', payload, {
          headers: {
            shopObjectId: util.sanitizeShopObjectId(this.shopObjectId),
            uid: this.uid,
          }
        })

        this.$toast.success('บันทึกลำดับเรียบร้อย')
      } catch (err) {
        console.error(err)
        this.$toast.error('บันทึกลำดับไม่สำเร็จ')
      }
    },

    // ═══ SETTINGS ═══
    async loadOrderShopSetting() {
      const shopObjectId = localStorage.getItem('shopObjectId')
      try {
        const res = await pos({
          method: 'get',
          url: '/api/v3.0/shop/ordershopsetting',
          params: { shopObjectId },
          headers: { shopObjectId }
        })
        this.orderShopCollection = res?.data?.data || null
        this.isApplyPromotionAuto = this.orderShopCollection?.isApplyPromotionAuto || false
      } catch (err) {
        console.error('Failed to load shop settings', err)
      }
    },

    async updateAutoApplyPromotion(value) {
      try {
        const uid = localStorage.getItem('shopsUid')
        const objectIdToUse = this.orderShopCollection?.objectId || this.shopObjectId

        await pos({
          method: 'patch',
          url: `/api/v2.0/${uid}/OrderShopSetting/${objectIdToUse}`,
          headers: {
            shopObjectId: util.sanitizeShopObjectId(this.shopObjectId)
          },
          data: { isApplyPromotionAuto: value }
        })
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
/* ═══ PAGE ═══ */
.page-promotion-sort {
  min-height: calc(100vh - 140px);
}

/* ═══ STATUS FILTER PILLS ═══ */
.sort-status-filter {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.sort-filter-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 100px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
}
.sort-filter-btn:hover {
  background: #f5f5f5;
}
.sort-filter-btn--active {
  background: #e8f5e9;
  border-color: var(--success);
  color: var(--success);
}
.sort-filter-btn--green.sort-filter-btn--active {
  background: #e8f5e9;
  border-color: #28a745;
  color: #28a745;
}
.sort-filter-btn--red.sort-filter-btn--active {
  background: #fff3e0;
  border-color: #e65100;
  color: #e65100;
}
.sort-filter-count {
  background: rgba(0, 0, 0, 0.08);
  padding: 1px 8px;
  border-radius: 100px;
  font-size: 11px;
  margin-left: 6px;
}
.sort-filter-btn--active .sort-filter-count {
  background: rgba(0, 0, 0, 0.1);
}

/* ═══ PROMOTION LIST ═══ */
.promotion-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ═══ CARD ═══ */
.promo-card {
  background: #fff;
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid #eee;
  transition: all 0.25s ease;
  cursor: grab;
}
.promo-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-color: #d0d0d0;
}
.promo-card--expired {
  opacity: 0.55;
}
.promo-card--expired:hover {
  opacity: 0.75;
}

/* ═══ DRAG HANDLE ═══ */
.drag-handle {
  cursor: grab;
}
.drag-handle-icon {
  font-size: 20px;
  color: #bbb;
  cursor: grab;
  user-select: none;
  width: 24px;
  text-align: center;
  transition: color 0.15s;
}
.promo-card:hover .drag-handle-icon {
  color: #666;
}
.drag-ghost {
  opacity: 0.3;
  background: #e8f5e9 !important;
}

/* ═══ IMAGE ═══ */
.promo-image {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #eee;
  flex-shrink: 0;
}

/* ═══ CONTENT ═══ */
.promo-title {
  font-weight: 700;
  font-size: 14px;
  color: #1a1a2e;
}
.promo-type {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.promo-date {
  font-size: 11.5px;
  color: #999;
  margin-top: 3px;
}

/* ═══ BADGE ═══ */
.sort-badge {
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* ═══ TABS ═══ */
.promo-tab-title {
  font-weight: 600;
  font-size: 13px;
}

/* ═══ INFO BOX ═══ */
.promo-info-box {
  border-radius: 12px;
}
.promo-info-title {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
}
.promo-info-desc {
  color: #555;
  font-size: 13px;
}
.promo-step {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 13px;
}
.step-desc {
  margin-left: 6px;
  color: #777;
  font-size: 12px;
}

/* ═══ EMPTY STATE ═══ */
.empty-state {
  text-align: center;
  padding: 48px 20px;
  color: #6b7280;
}
.empty-icon {
  font-size: 40px;
  color: #d1d5db;
  margin-bottom: 12px;
  display: block;
}
.empty-title {
  font-weight: 700;
  font-size: 16px;
  color: #374151;
}
.empty-desc {
  font-size: 13px;
  margin-top: 4px;
  color: #9ca3af;
}

/* ═══ RESPONSIVE ═══ */
@media (max-width: 576px) {
  .promo-image {
    width: 52px;
    height: 52px;
    border-radius: 8px;
  }
  .promo-title {
    font-size: 13px;
  }
  .sort-badge {
    font-size: 10px;
    padding: 3px 8px;
  }
  .sort-filter-btn {
    font-size: 12px;
    padding: 5px 12px;
  }
}
</style>
